import { usePackageGrantsQuery } from '../../lib/Queries';
import Loading from '../Loading';
import EditGrantPanel from './EditGrantPanel.jsx';
import React, {
    forwardRef,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline/index.js';
import {
    truncate,
    truncateWords,
    ucfirst,
    uniqueArray,
} from '../../lib/Utils.js';
import {
    humanGrantRedeemableAmount,
    humanRedeemableAmount,
    PackageTypes,
} from '../../lib/PackageHelpers.js';
import { formatDateShort } from '../../lib/DateTime.js';
import Select from '~/components/Select';

export const GrantColumnHeadings = ({
    showPackageName,
    nameHeading,
    isRenewing,
}) => {
    return (
        <div className="flex items-center justify-between mx-6">
            <div className="flex-1 column-label">Client</div>
            {!!showPackageName && (
                <div className="flex-1 column-label">
                    {nameHeading || 'Package'}
                </div>
            )}
            <div className="flex-1 column-label">Status</div>
            <div className="flex-1 column-label">Remaining</div>
            <div className="flex-1 column-label">
                {isRenewing ? 'Renews' : 'Expires'}
            </div>
            <div className="w-6"></div>
        </div>
    );
};

export const GrantListRow = ({ grant, showPackageName, isRenewing }) => {
    return (
        <div className="flex items-center justify-between">
            <div className="flex-1">
                <div>
                    {truncateWords(grant.client?.name || 'No client', 15)}
                </div>
                <div className="text-gray-500 text-sm">
                    {truncate(grant.client?.email, 15)}
                </div>
            </div>
            {!!showPackageName && (
                <div className="flex-1">
                    {grant.package?.name || 'Unknown Package'}
                </div>
            )}
            <div className="flex-1">{<StatusBadge grant={grant} />}</div>
            <div className="flex-1">{humanGrantRedeemableAmount(grant)}</div>
            <div className="flex-1">
                {grant?.expires_at
                    ? formatDateShort(grant?.expires_at)
                    : 'No expiration'}
                {isRenewing && !grant?.next_renewal_at && (
                    <div className="text-sm text-gray-500">Expiring</div>
                )}
            </div>
        </div>
    );
};

export const StatusBadge = ({ grant }) => {
    let status = 'active';

    if (
        grant.remaining_balance <= 0 &&
        grant.redeemable_for?.type !== 'nothing'
    ) {
        status = 'empty';
    } else if (grant.is_expired) {
        status = 'expired';
    }

    if (
        !grant.next_renewal_at &&
        grant.package.type === PackageTypes.SUBSCRIPTION
    ) {
        status = grant.is_expired ? 'expired' : 'ending';
    }

    let color = 'bg-red-100 !text-red-700';
    if (status === 'active') {
        color = 'bg-green-100 !text-green-700';
    }
    if (status === 'ending') {
        color = 'bg-yellow-100 !text-yellow-700';
    }

    return (
        <div className="flex">
            <div className={`tag ${color}`}>{ucfirst(status)}</div>
        </div>
    );
};

const GrantRow = ({
    grant,
    isFirst,
    onClick,
    content,
    refetch,
    manualOnClick,
}) => {
    return (
        <div
            className={`${!isFirst ? 'border-t' : ''} flex ml-6 pr-6 py-3 items-center justify-between`}
        >
            <div
                className="flex-1 cursor-pointer"
                onClick={() => !manualOnClick && onClick()}
            >
                {typeof content === 'function'
                    ? content({
                          grant,
                          statusBadge: <StatusBadge grant={grant} />,
                          refetch,
                          onClick,
                      })
                    : 'Error, not  function'}
            </div>
            {!manualOnClick && (
                <div onClick={onClick}>
                    <ChevronRightIcon className="w-5 h-5 text-gray-300" />
                </div>
            )}
        </div>
    );
};

const GrantFilters = ({ filters, setFilters, plural, grants }) => {
    const packageOptions = useMemo(() => {
        const uniqueGrants = grants.filter((grant, idx) => {
            return (
                grants.findIndex(g => g.package_id === grant.package_id) === idx
            );
        });

        if (uniqueGrants.length <= 1) {
            return null;
        }

        return [
            { value: '', label: `All ${ucfirst(plural) || 'packages'}` },
            ...uniqueGrants.map(grant => ({
                value: grant.package_id,
                label: grant.package?.name,
            })),
        ];
    }, [grants]);

    return (
        <div className="flex items-center justify-end mb-4">
            {!!packageOptions && (
                <Select
                    options={packageOptions}
                    value={filters?.package}
                    onChange={value =>
                        setFilters(old => ({ ...old, package: value }))
                    }
                    className="mr-4"
                />
            )}

            <Select
                options={{
                    '': 'All Statuses',
                    active: 'Active',
                    expired: 'Expired',
                    empty: 'Empty',
                }}
                value={filters?.status}
                onChange={value =>
                    setFilters(old => ({ ...old, status: value }))
                }
            />
        </div>
    );
};

const GrantList = forwardRef(
    (
        {
            search,
            hideWhenEmpty,
            rowContent,
            headerContent,
            footerContent,
            manualOnClick,
            plural,
            showFilters,
        },
        ref,
    ) => {
        const { data, isLoading, refetch } = usePackageGrantsQuery(search);
        let grants = data || [];
        const [showNewGrantPanel, setShowNewGrantPanel] = useState(false);
        const [editGrant, setEditGrant] = useState('0');
        const [filters, setFilters] = useState({});

        function showNewGrant() {
            setEditGrant(null);
            setShowNewGrantPanel(true);
        }

        useImperativeHandle(ref, () => ({
            showNewGrant,
            refetch,
        }));

        if (!grants.length && hideWhenEmpty) {
            return null;
        }

        function applyFilters(grant) {
            let statusMatches = true;
            if (filters?.status) {
                const notExpired = !grant.is_expired;
                const empty = grant.remaining_balance <= 0;
                if (filters.status === 'active') {
                    statusMatches = notExpired && !empty;
                } else if (filters.status === 'expired') {
                    statusMatches = !notExpired;
                } else if (filters.status === 'empty') {
                    statusMatches = empty;
                }
            }

            let packageMatches = true;
            if (filters?.package) {
                packageMatches =
                    parseInt(grant.package_id) === parseInt(filters.package);
            }

            return statusMatches && packageMatches;
        }

        const filteredGrants = grants.filter(applyFilters);

        return (
            <div>
                {!!showFilters && (
                    <GrantFilters
                        filters={filters}
                        setFilters={setFilters}
                        plural={plural}
                        grants={grants}
                    />
                )}
                {headerContent({
                    grants,
                    showNewGrant: showNewGrant,
                })}
                <div className="panel !max-w-none">
                    {isLoading && <Loading className="p-6" />}
                    {!isLoading && filteredGrants.length === 0 && (
                        <div className="p-6 text-gray-500">
                            {!grants?.length && (
                                <>
                                    There are no clients with{' '}
                                    {plural || 'packages'}
                                </>
                            )}
                            {grants.length > 0 && (
                                <>
                                    There are no clients matching your filters
                                    with {plural || 'packages'}
                                </>
                            )}
                        </div>
                    )}
                    {typeof filteredGrants.map === 'function' &&
                        filteredGrants.map((grant, idx) => (
                            <GrantRow
                                grant={grant}
                                key={grant.id}
                                isFirst={idx === 0}
                                onClick={() => {
                                    setShowNewGrantPanel(true);
                                    setEditGrant(grant);
                                }}
                                refetch={refetch}
                                content={rowContent}
                                manualOnClick={manualOnClick}
                            />
                        ))}
                </div>
                {typeof footerContent === 'function' &&
                    footerContent({
                        grants,
                        showNewGrant: showNewGrant,
                    })}
                <EditGrantPanel
                    isOpen={showNewGrantPanel}
                    onClose={() => {
                        setShowNewGrantPanel(false);
                        setEditGrant(null);
                    }}
                    grant={editGrant}
                    packageId={search.packageId}
                    refetch={refetch}
                />
            </div>
        );
    },
);

export default GrantList;
